import { useOutletContext } from "react-router";

import { Box, Divider, Stack, Typography } from "@mui/material";
import { ClientContent } from "@shared";

import { ClientDetailResponse } from "@/api";
import { FormikAutocompleteSelect } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocompleteSelect";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { formatTimestamp } from "@/common/utils/formatTimestamp";
import { ClientAccordion } from "@/pages/clients/client/components/tabs/overview-tab/components/client-accordion/ClientAccordion";
import { useTranslation } from "@/translations";

import { useStatusCounselor } from "./hooks/useStatusCounselor";

export function StatusCounselorView() {
  const client = useOutletContext<ClientDetailResponse>();
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { formik, statusHistory, statusOptions, statusLabelOf, counselorOptions } = useStatusCounselor(
    client.clientId,
    {
      status: client.status ?? null,
      assignedCounselorId: client.assignedCounselorId ?? null,
    }
  );

  return (
    <ClientContent formik={formik}>
      <Stack flex={1} sx={{ maxHeight: "100%", overflow: "hidden" }}>
        <Stack gap={3} bgcolor="background.paper" p={2} flexShrink={0}>
          <Typography variant="h5">{t("clients.client.masterData.statusCounselor")}</Typography>

          <Stack direction="row" gap={2}>
            <FormikAutocompleteSelect
              formik={formik}
              fieldName="status"
              label={t("clients.client.masterData.statusCounselorSection.status")}
              options={statusOptions}
            />

            <FormikAutocompleteSelect
              formik={formik}
              fieldName="assignedCounselorId"
              label={t("clients.client.masterData.statusCounselorSection.counselor")}
              options={counselorOptions}
            />
          </Stack>
        </Stack>

        <Box mt={2} flex={1} overflow="auto">
          <ClientAccordion
            title={t("clients.client.masterData.statusCounselorSection.statusHistory")}
            accordionKey="client.statusHistory"
            defaultExpanded
          >
            <Stack divider={<Divider sx={{ mx: 2 }} />}>
              {statusHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary" px={2} py={1}>
                  –
                </Typography>
              ) : (
                statusHistory.map((entry, idx) => (
                  <Stack key={idx} direction="row" gap={4} px={2} py={1.5}>
                    <Typography variant="body2" sx={{ pl: 1 }}>
                      {formatTimestamp(entry.timestamp, t, language)}
                    </Typography>
                    <Typography variant="body2">
                      {idx === 0
                        ? t("clients.client.masterData.statusCounselorSection.created")
                        : statusLabelOf(entry.status)}
                    </Typography>
                  </Stack>
                ))
              )}
            </Stack>
          </ClientAccordion>
        </Box>
      </Stack>
    </ClientContent>
  );
}
